/**
 * DraftListPage
 *
 * Lista de requisições em rascunho (mesmo layout da fila de revisão técnica).
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Search,
  User,
  Clock,
  ChevronRight,
  LayoutList,
  AlertCircle,
  Plus,
} from 'lucide-react';
import { useRequisitions } from '../../../hooks/usePurchases';
import './ReviewListPage.css';
import '../Prospecting/ProspectingPage.css';

const DRAFT_LIST_FILTERS = { status: 'draft', limit: 500, page: 1 };

const DraftListPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const { requisitions, loading, error, refetch } = useRequisitions(DRAFT_LIST_FILTERS);

  const getPriorityConfig = (priority) => {
    const configs = {
      critical: { label: 'Crítico', className: 'priority-critical' },
      urgent: { label: 'Urgente', className: 'priority-urgent' },
      normal: { label: 'Normal', className: 'priority-normal' },
    };
    return configs[priority] || configs.normal;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const filteredQueue = useMemo(() => {
    if (!requisitions) return [];

    return requisitions.filter((req) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        req.title?.toLowerCase().includes(searchLower) ||
        req.id?.toString().includes(searchTerm) ||
        req.requester_name?.toLowerCase().includes(searchLower)
      );
    });
  }, [requisitions, searchTerm]);

  return (
    <div className="review-list-page">
      <div className="finance-list-header">
        <div>
          <h1 className="finance-list-title">
            Requisições em <span className="finance-list-title-highlight">Rascunho</span>
          </h1>
          <p className="finance-list-subtitle">
            Continue editando ou inicie uma nova requisição de compra
          </p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
          <button
            type="button"
            className="prospecting-btn prospecting-btn-primary"
            onClick={() => navigate('/erp/purchases/new-request')}
          >
            <Plus size={18} strokeWidth={2.5} />
            Nova requisição
          </button>
          <button
            type="button"
            className="finance-list-back-button"
            onClick={() => navigate('/erp/purchases')}
          >
            <ArrowLeft size={16} />
            Painel de Compras
          </button>
        </div>
      </div>

      <div className="finance-list-toolbar">
        <div className="finance-list-search">
          <Search className="finance-list-search-icon" size={18} />
          <input
            type="text"
            placeholder="Buscar por ID, título ou solicitante..."
            className="finance-list-search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading && (
        <div className="finance-list-loading">
          <div className="finance-list-loading-spinner" />
          <p>Carregando rascunhos...</p>
        </div>
      )}

      {error && !loading && (
        <div className="finance-list-error">
          <AlertCircle size={24} />
          <p>{error}</p>
          <button type="button" onClick={() => refetch()} className="finance-list-retry-button">
            Tentar Novamente
          </button>
        </div>
      )}

      {!loading && !error && filteredQueue.length === 0 && (
        <div className="finance-list-empty">
          <LayoutList size={48} />
          <p>Nenhum rascunho encontrado</p>
          <span>
            {searchTerm
              ? 'Tente ajustar os termos de busca'
              : 'Crie uma nova requisição para começar'}
          </span>
          {!searchTerm && (
            <button
              type="button"
              className="prospecting-btn prospecting-btn-primary"
              style={{ marginTop: '1.5rem' }}
              onClick={() => navigate('/erp/purchases/new-request')}
            >
              <Plus size={18} strokeWidth={2.5} />
              Nova requisição
            </button>
          )}
        </div>
      )}

      {!loading && !error && filteredQueue.length > 0 && (
        <div className="finance-list-grid">
          {filteredQueue.map((req) => {
            const priorityConfig = getPriorityConfig(req.priority || 'normal');
            const itemsCount = req.items_count || req.items?.length || 0;

            return (
              <button
                key={req.id}
                type="button"
                className="finance-list-card"
                onClick={() => navigate(`/erp/purchases/new-request/${req.id}`)}
              >
                <div className={`finance-card-glow ${priorityConfig.className}`} />

                <div className="finance-card-header">
                  <div className="finance-card-badges">
                    <span className="finance-priority-badge status-draft-badge">Rascunho</span>
                  </div>
                  <span className="finance-card-id">#{req.id}</span>
                </div>

                <h3 className="finance-card-title">{req.title || 'Sem título'}</h3>

                <div className="finance-card-meta">
                  <div className="finance-card-meta-item">
                    <User size={12} />
                    <span>{req.requester_name || 'N/A'}</span>
                  </div>
                  <div className="finance-card-meta-item">
                    <Clock size={12} />
                    <span>{formatDate(req.created_at)}</span>
                  </div>
                </div>

                <div className="finance-card-footer">
                  <div className="finance-card-value">
                    <p className="finance-card-value-label">Itens</p>
                    <p className="finance-card-value-amount">{itemsCount}</p>
                  </div>
                  <div className="finance-card-arrow">
                    <ChevronRight size={18} />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DraftListPage;
